// ── V155: 오늘의 작업 → 위챗으로 보낼 글 (발주서 '위챗으로 보내기'와 같은 방식) ──────────
// V156: '회의 주제' 묶음은 없앴다 (회의는 회의 탭). 회의에서 온 테스크는 '🗓 회의 …' 줄을 붙인다
// 위챗은 웹에서 바로 보내는 공개 기능이 없다 → 글을 만들어 휴대폰은 공유창, 컴퓨터는 복사 후 붙여넣기.
//   mode 'bi'  한국어 + 중국어 같이 (기본) · 'zh' 중국어만 · 'ko' 한국어만
//   레시피 이름·재료·칸 이름은 번역해 넣고, 직접 적은 글(작업 이름·내용·담당자)은 적은 그대로 보낸다
//   원가·가격은 넣지 않는다. 너무 길면 나눠 보낸다 (위챗 한 번에 보내기 좋은 길이)
import { zhOf } from '../i18n/BiName';
import { scaleIngredients, fmtAmount, fmtNum, fmtDay, recipeBase } from './workCalc';
import { fmtDue } from './workMeta';

const hhmm = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// 칸 이름 — mode에 따라
const T = (mode, ko, zh) => (mode === 'zh' ? zh : mode === 'ko' ? ko : (ko === zh ? ko : `${ko}·${zh}`));
// 이름 — 'bi'는 '양파 / 洋葱' (중국어가 없으면 한국어만)
const nm = (mode, ko, zh) => {
  const k = String(ko || '').trim(); const z = String(zh || '').trim();
  if (mode === 'zh') return z || k;
  if (mode === 'ko') return k || z;
  return z && z !== k ? `${k} / ${z}` : k;
};
const amountLang = (mode) => (mode === 'zh' ? 'zh' : 'ko');

/**
 * @param {object} o
 *   items      [{ id, kind, recipeId, name, name_zh, grams, mode, text, note, owner, due, from, done, at, by }]
 *   recipesById Map · zhMap 단가표 한→중 맵
 *   date 'YYYY-MM-DD' · today 'YYYY-MM-DD' (기한을 '오늘·내일'로 쓰려고) · memo · storeName · greeting 맨 위에 붙일 말
 *   mode 'bi' | 'zh' | 'ko' · withDone ✅⬜ · withMeta 내용·담당·기한 · withIngs 재료
 * @returns {{ parts: string[], count: number }}
 */
export function buildWorkText({
  items = [], recipesById = new Map(), zhMap = new Map(), date = '', today = '', memo = '', storeName = '',
  greeting = '', mode = 'bi', withDone = true, withMeta = true, withIngs = false, maxLen = 1800,
} = {}) {
  const list = (items || []).filter(Boolean);
  const done = list.filter(i => i.done).length;

  // ── 머리 ──
  const head = [];
  const g = String(greeting || '').trim();
  if (g) head.push(g, '');
  head.push(`📋 ${T(mode, '오늘의 작업', '今日工作').replace('·', ' ')}`);
  const dayKo = fmtDay(date, 'ko'); const dayZh = fmtDay(date, 'zh');
  const day = mode === 'zh' ? dayZh : mode === 'ko' ? dayKo : (date ? `${dayKo} · ${dayZh}` : '');
  head.push([day, String(storeName || '').trim()].filter(Boolean).join(' · '));
  if (withDone) head.push(mode === 'zh' ? `完成 ${done}/${list.length}` : mode === 'ko' ? `완료 ${done}/${list.length}` : `완료·完成 ${done}/${list.length}`);

  // ── 항목 ──
  const one = (it, n) => {
    const r = it.kind === 'recipe' ? recipesById.get(it.recipeId) : null;
    const box = withDone ? (it.done ? '✅ ' : '⬜ ') : '';
    let title;
    if (it.kind === 'text') title = String(it.text || '').trim();
    else {
      title = nm(mode, r?.name || it.name, r?.name_zh || it.name_zh);
      const gr = Number(it.grams) || 0;
      if (gr > 0) {
        const sv = Number(r?.serving_g) || 0;
        const servTxt = sv > 0
          ? (mode === 'zh' ? `（约${fmtNum(gr / sv)}份）` : mode === 'ko' ? ` (≈${fmtNum(gr / sv)}인분)` : ` (≈${fmtNum(gr / sv)}인분/份)`)
          : '';
        title += `  ${fmtAmount(gr, 'g', amountLang(mode))}${servTxt}`;
      }
    }
    const lines = [`${n}. ${box}${title}`];
    if (withDone && it.done && (it.at || it.by)) lines.push(`   ✓ ${[hhmm(it.at), it.by || ''].filter(Boolean).join(' ')}`);
    if (withMeta) {
      const meta = [];
      if (it.owner) meta.push(`👤 ${T(mode, '담당', '负责')} ${it.owner}`);
      const due = fmtDue(it, mode, today);
      if (due) meta.push(`⏰ ${T(mode, '마감', '截止')} ${due}`);
      if (meta.length) lines.push(`   ${meta.join(' · ')}`);
      if (it.from) lines.push(`   🗓 ${T(mode, '회의', '会议')}${it.from.title ? ` ${it.from.title}` : ''}`);
      if (it.note) {
        String(it.note).split('\n').map(s => s.trim()).filter(Boolean).forEach((s, k) => {
          lines.push(`   ${k === 0 ? `📝 ${s}` : `   ${s}`}`);
        });
      }
    }
    if (withIngs && r && recipeBase(r) > 0) {
      const ings = scaleIngredients(r, Number(it.grams) || 0)
        .map(ing => `${nm(mode, ing.name, zhOf(zhMap, ing.name))} ${fmtAmount(ing.qty, ing.unit, amountLang(mode))}`);
      if (ings.length) lines.push(`   ${T(mode, '재료', '用料')}: ${ings.join(' · ')}`);
    }
    return lines.join('\n');
  };

  const blocks = list.map((it, k) => ({ text: one(it, k + 1) }));

  // ── 꼬리 ──
  const tail = [];
  const m = String(memo || '').trim();
  if (m) tail.push(`📝 ${T(mode, '메모', '备注')}: ${m}`);
  tail.push(mode === 'zh' ? `共 ${list.length} 项` : mode === 'ko' ? `총 ${list.length}개` : `총 ${list.length}개 · 共 ${list.length} 项`);

  // ── 나누기 ── (항목 중간에서 자르지 않는다)
  const parts = [];
  let cur = [...head, ''];
  let curLen = cur.join('\n').length;
  let curHasItem = false;
  for (const b of blocks) {
    if (curHasItem && curLen + b.text.length + 1 > maxLen) {
      parts.push(cur.join('\n'));
      cur = [];
      curLen = 0;
      curHasItem = false;
    }
    cur.push(b.text);
    curLen += b.text.length + 1;
    curHasItem = true;
  }
  cur.push('', ...tail);
  parts.push(cur.join('\n'));
  const clean = parts.map(p => p.replace(/\n{3,}/g, '\n\n').trim());
  if (clean.length > 1) return { parts: clean.map((t, i) => `(${i + 1}/${clean.length})\n${t}`), count: list.length };
  return { parts: clean, count: list.length };
}

export default buildWorkText;
